import React, { memo, useCallback } from 'react';

import { tableColors } from '../lib';
import { CanvasTable } from './canvasTable';

interface SchemaTableProps {
  schemaTable: GM.ReservesSchemas.SchemaTable | GM.ReservesSchemas.SchemaListTable;
  selectedTableIds: number[];
  onClick: (id: number) => void;
  tableSchedule?: GM.ReservesSchemas.TableSchedule;
  personImage: HTMLImageElement;
  restaurantTz: string;
  // isMobile?: boolean;
}

function SchemaTableComponent({
  schemaTable,
  onClick,
  tableSchedule,
  personImage,
  restaurantTz,
  // isMobile = false,
}: SchemaTableProps) {
  const { id } = schemaTable.table;
  const { attributes, next } = tableSchedule || {};

  // Мемоизируем обработчик клика для стабильности ссылки
  const handleClick = useCallback(() => {
    onClick(id);
  }, [onClick, id]);

  return (
    <CanvasTable
      nextReserveItemDate={next?.date}
      schemaTable={schemaTable}
      onClick={handleClick}
      personImage={personImage}
      shadow={schemaTable.type === 'TABLE'}
      colors={tableColors[attributes?.stateV2 || 'FREE']}
      restaurantTz={restaurantTz}
      // isMobile={isMobile}
    />
  );
}

// Кастомная функция сравнения для оптимизации re-render
// Перерисовываем только если изменились релевантные пропсы для этого конкретного стола
function areEqual(prevProps: SchemaTableProps, nextProps: SchemaTableProps): boolean {
  // Сравниваем ID стола
  if (prevProps.schemaTable.table.id !== nextProps.schemaTable.table.id) {
    return false;
  }

  // Сравниваем структуру schemaTable (позиция, размер)
  if (
    prevProps.schemaTable.x !== nextProps.schemaTable.x ||
    prevProps.schemaTable.y !== nextProps.schemaTable.y ||
    prevProps.schemaTable.width !== nextProps.schemaTable.width ||
    prevProps.schemaTable.height !== nextProps.schemaTable.height ||
    prevProps.schemaTable.type !== nextProps.schemaTable.type
  ) {
    return false;
  }

  // Сравниваем расписание стола - только релевантные поля
  const prevSchedule = prevProps.tableSchedule;
  const nextSchedule = nextProps.tableSchedule;

  if (!prevSchedule && !nextSchedule) {
    // Оба undefined - одинаково
  } else if (!prevSchedule || !nextSchedule) {
    return false; // Один есть, другого нет
  } else {
    // Сравниваем только те поля, которые влияют на отображение
    const prevAttrs = prevSchedule.attributes;
    const nextAttrs = nextSchedule.attributes;

    if (
      prevAttrs?.stateV2 !== nextAttrs?.stateV2 ||
      prevAttrs?.reserveTimeStr !== nextAttrs?.reserveTimeStr ||
      prevAttrs?.reserveTimeStatus !== nextAttrs?.reserveTimeStatus ||
      prevAttrs?.forcedTableTimeStatus !== nextAttrs?.forcedTableTimeStatus
    ) {
      return false;
    }

    // Сравниваем next?.date для CanvasTableWarningBar
    if (prevSchedule.next?.date !== nextSchedule.next?.date) {
      return false;
    }
  }

  // Сравниваем остальные пропсы
  if (
    prevProps.personImage !== nextProps.personImage ||
    prevProps.restaurantTz !== nextProps.restaurantTz
    // || prevProps.isMobile !== nextProps.isMobile
  ) {
    return false;
  }

  // onClick функция может меняться, но это не должно вызывать re-render,
  // так как она используется только при клике
  // Однако для безопасности сравниваем selectedTableIds через schemaTable.table.id
  // (это уже проверено выше)

  return true;
}

export const SchemaTable = memo(SchemaTableComponent, areEqual);
